import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { DOC } from "@/constants/testIds";
import { useActiveContext } from "@/context/AdaContext";
import {
  Upload, FolderPlus, Trash2, Eye, FileDown, Lock, FileText, FileSpreadsheet,
  Image as ImageIcon, Presentation, FileType, ArrowLeft, X, Search, Folder,
  Pencil, Link2, FileType2, CheckCircle2, Circle, Loader2, Archive, KeyRound,
  Flame, ChevronUp, ChevronDown, ChevronsUpDown,
} from "lucide-react";
import BulkUpload from "@/components/BulkUpload";
import Confirm from "@/components/Confirm";
import ManageAccessDialog from "@/components/ManageAccessDialog";
import RequestAccessModal from "@/components/RequestAccessModal";
import WarmCacheButton from "@/components/WarmCacheButton";
import { toast } from "@/components/Toaster";
import { useFileProgress } from "@/context/FileProgressContext";

/**
 * SortHeader — clickable header cell with directional chevron.
 *
 * Behaviour:
 *   • Inactive headers show a faint two-way chevron (so the column reads
 *     as "sortable" without screaming about it).
 *   • Active header shows a solid up/down chevron matching the direction.
 *   • Hover lifts the label to full foreground; press uses `aria-sort` so
 *     screen readers announce the current sort state.
 *
 * The wider header strip keeps the existing label-mono typography so the
 * new affordance blends into the existing chrome.
 */
const SortHeader = ({ label, active, dir, onClick, testId }) => {
  const ariaSort = active ? (dir === "asc" ? "ascending" : "descending") : "none";
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      data-dir={active ? dir : ""}
      onClick={onClick}
      aria-sort={ariaSort}
      className={`px-4 py-3 text-left inline-flex items-center gap-1.5 transition-colors hover:text-foreground ${
        active ? "text-foreground" : "text-muted-foreground"
      }`}
    >
      <span>{label}</span>
      {active ? (
        dir === "asc" ? (
          <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
      )}
    </button>
  );
};

export default function DocumentsTab({ roomId, isAdmin, room }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [path, setPath] = useState([]); // [{id, name}] breadcrumb stack; [] = root
  const [showFolder, setShowFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [showZip, setShowZip] = useState(false);
  const [err, setErr] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [accessDoc, setAccessDoc] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [requestScope, setRequestScope] = useState(null); // {docId, name, isFolder} | null
  const [renameValue, setRenameValue] = useState("");
  const [dragOverId, setDragOverId] = useState(null); // folder id being hovered (or 'ROOT')
  const [clipboard, setClipboard] = useState(null); // {mode:'copy'|'cut', doc}
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y, doc}
  const [myPerms, setMyPerms] = useState(null); // { is_admin, allow_downloads, room_level, overrides }
  const fileProgress = useFileProgress();
  // Column-header sorting. Clicking a header cycles asc -> desc -> default.
  // `default` falls back to the long-standing alphabetical sort so users
  // never get stuck without a deterministic order.
  // Folders always stay grouped above files regardless of sort direction —
  // this keeps the file-browser metaphor intact (Finder / Drive behaviour).
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc" | null

  const currentParentId = path.length ? path[path.length - 1].id : null;
  const currentFolderName = path.length ? path[path.length - 1].name : null;

  // Publish current folder focus to Ada — so questions like "what's
  // in this folder?", "any tax stuff here?", or "summarise this
  // folder" work against the folder the user has open in the UI.
  useActiveContext(
    currentParentId
      ? { type: "folder", folder_id: currentParentId, name: currentFolderName }
      : null,
  );

  const load = async () => {
    const { data } = await api.get(`/rooms/${roomId}/documents`);
    setDocs(data);
  };
  useEffect(() => { load(); }, [roomId]);

  // Live-refresh effective permissions:
  //   1. Initial mount
  //   2. Every 30 seconds while the tab is open
  //   3. Whenever the browser tab regains focus
  //   4. Whenever the documents list changes (Apply in PermissionsTab triggers
  //      a server-side change; admins regularly look at both tabs so the
  //      viewer's session shouldn't sit on stale perms).
  useEffect(() => {
    let alive = true;
    const fetchPerms = async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/my-permissions`);
        if (alive) setMyPerms(data);
      } catch {
        if (alive) setMyPerms(null);
      }
    };
    fetchPerms();
    const interval = setInterval(fetchPerms, 30_000);
    const onFocus = () => fetchPerms();
    const onVisible = () => { if (document.visibilityState === "visible") fetchPerms(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [roomId]);

  // Sync the breadcrumb path with the `folder_path` URL search param so
  // browser-back / forward and notification deep-links restore the exact
  // folder the user was in. Format: ?folder_path=id1,id2,id3 (root = absent).
  useEffect(() => {
    const raw = searchParams.get("folder_path") || "";
    const ids = raw.split(",").filter(Boolean);
    const currentIds = path.map((p) => p.id);
    if (ids.length === currentIds.length && ids.every((id, i) => id === currentIds[i])) return;
    if (!docs.length) return;
    const byId = new Map(docs.filter((d) => d.type === "folder").map((d) => [d.id, d]));
    const next = [];
    for (const id of ids) {
      const f = byId.get(id);
      if (!f) break;
      next.push({ id: f.id, name: f.name });
    }
    setPath(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, docs]);

  const pushPath = (next) => {
    setPath(next);
    const sp = new URLSearchParams(searchParams);
    if (next.length) sp.set("folder_path", next.map((p) => p.id).join(","));
    else sp.delete("folder_path");
    setSearchParams(sp);
  };

  // Effective permissions for a given file (doc-level override beats room-level).
  const permsFor = (d) => {
    if (!myPerms) return null;
    const base = myPerms.overrides?.[d.id] || myPerms.room_level;
    return {
      ...base,
      // allow_downloads is a room-level toggle that overrides per-user perms.
      pdf: base.pdf && (myPerms.allow_downloads || myPerms.is_admin),
      encrypted: base.encrypted && (myPerms.allow_downloads || myPerms.is_admin),
      original: base.original && (myPerms.allow_downloads || myPerms.is_admin),
    };
  };

  // Debounced search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchHits(null); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/rooms/${roomId}/search`, { params: { q: searchQ.trim() } });
        setSearchHits(data);
      } catch { setSearchHits([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQ, roomId]);

  // Sort comparator that honours the current header click state.
  // Folders are always grouped above files; within each group we sort by
  // the active column (or fall back to alphabetical when no sort is set
  // or when the key isn't applicable, e.g. "pages" for a folder).
  const childCount = (folderId) =>
    docs.filter((x) => x.parent_id === folderId).length;
  const sortVal = (d, key) => {
    if (key === "name") return (d.name || "").toLowerCase();
    if (key === "size") return d.type === "folder" ? childCount(d.id) : (d.size || 0);
    if (key === "pages") return d.type === "folder" ? -1 : (d.pages || 0);
    return 0;
  };
  const cmp = (a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    if (sortKey && sortDir) {
      const va = sortVal(a, sortKey);
      const vb = sortVal(b, sortKey);
      let diff;
      if (typeof va === "number" && typeof vb === "number") diff = va - vb;
      else diff = String(va).localeCompare(String(vb), undefined, { numeric: true });
      if (diff !== 0) return sortDir === "asc" ? diff : -diff;
    }
    // Tie-break (or default) — alphabetical, numeric-aware.
    return (a.name || "").localeCompare(b.name || "", undefined, { numeric: true });
  };

  const visible = docs
    .filter((d) => (d.parent_id || null) === currentParentId)
    .slice()
    .sort(cmp);

  // Header click handler — asc -> desc -> reset to default ("name asc").
  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") setSortDir("desc");
    else if (sortDir === "desc") { setSortKey("name"); setSortDir("asc"); }
    else { setSortDir("asc"); }
  };

  const enter = (folder) => pushPath([...path, { id: folder.id, name: folder.name }]);
  const goRoot = () => pushPath([]);
  const goTo = (i) => pushPath(path.slice(0, i + 1));

  const createFolder = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rooms/${roomId}/folders`, {
        name: folderName, parent_id: currentParentId,
      });
      setShowFolder(false); setFolderName("");
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  const startRename = (d) => { setRenameId(d.id); setRenameValue(d.name); };
  const cancelRename = () => { setRenameId(null); setRenameValue(""); };
  const saveRename = async (d) => {
    const next = renameValue.trim();
    if (!next || next === d.name) { cancelRename(); return; }
    try {
      await api.patch(`/rooms/${roomId}/documents/${d.id}`, { name: next });
      cancelRename();
      await load();
      toast.success(d.type === "folder" ? "Folder renamed" : "File renamed");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // ---- Drag & drop move (with undo) ----
  const dragMove = async (sourceId, targetParentId) => {
    if (!sourceId) return;
    const src = docs.find((x) => x.id === sourceId);
    if (!src) return;
    if ((src.parent_id || null) === (targetParentId || null)) return; // no-op
    const previousParentId = src.parent_id || null;
    try {
      await api.patch(`/rooms/${roomId}/documents/${sourceId}`, { parent_id: targetParentId });
      await load();
      const targetName = targetParentId
        ? (docs.find((x) => x.id === targetParentId)?.name || "folder")
        : "the data room root";
      toast.success(`Moved "${src.name}" to ${targetName}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.patch(`/rooms/${roomId}/documents/${sourceId}`, { parent_id: previousParentId });
              await load();
              toast.success(`Reverted "${src.name}"`);
            } catch (e) { toast.error(formatError(e)); }
          },
        },
      });
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const onRowDragStart = (e, d) => {
    if (!isAdmin) return;
    e.dataTransfer.setData("text/plain", d.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onFolderDragOver = (e, folderId) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(folderId);
  };
  const onFolderDragLeave = () => setDragOverId(null);
  const onFolderDrop = (e, folderId) => {
    if (!isAdmin) return;
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    setDragOverId(null);
    if (!id || id === folderId) return;
    const src = docs.find((x) => x.id === id);
    if (!src) return;
    if ((src.parent_id || null) === (folderId || null)) return; // no-op
    const targetName = folderId
      ? (docs.find((x) => x.id === folderId)?.name || "selected folder")
      : "the data room root";
    setConfirmState({
      title: src.type === "folder" ? "Move this folder?" : "Move this file?",
      message: `"${src.name}" will be moved to ${targetName}. You can undo right after, or move it back manually.`,
      confirmLabel: "Move here",
      destructive: false,
      onConfirm: async () => {
        await dragMove(id, folderId);
      },
    });
  };

  // ---- Clipboard (copy / cut / paste) ----
  const onCopy = (d) => { setClipboard({ mode: "copy", doc: d }); toast.success(`Copied "${d.name}" — paste into any folder`); setCtxMenu(null); };
  const onCut  = (d) => { setClipboard({ mode: "cut",  doc: d }); toast.success(`Cut "${d.name}" — paste into any folder`);  setCtxMenu(null); };
  const onPaste = async () => {
    setCtxMenu(null);
    if (!clipboard) return;
    const { mode, doc } = clipboard;
    const dest = currentParentId;
    try {
      if (mode === "copy") {
        await api.post(`/rooms/${roomId}/documents/${doc.id}/copy`, { parent_id: dest });
        toast.success(`Pasted copy of "${doc.name}"`);
      } else {
        const previousParentId = doc.parent_id || null;
        await api.patch(`/rooms/${roomId}/documents/${doc.id}`, { parent_id: dest });
        toast.success(`Moved "${doc.name}" here`, {
          duration: 6000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.patch(`/rooms/${roomId}/documents/${doc.id}`, { parent_id: previousParentId });
                await load();
                toast.success(`Reverted "${doc.name}"`);
              } catch (e) { toast.error(formatError(e)); }
            },
          },
        });
        setClipboard(null);
      }
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  // Context menu wiring.
  //
  // Files → everyone can right-click, but the visible actions are
  // filtered by permsFor(d) below (a view-only user simply sees an
  // empty / disabled menu, matching what they'd get on the row icons).
  // Folders → still admin-only, since the folder-level context actions
  // (rename, cut/paste, delete) don't have meaningful non-admin
  // equivalents yet.
  const openCtxMenu = (e, d) => {
    if (d.type === "folder" && !isAdmin) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, doc: d });
  };
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [ctxMenu]);

  // Keyboard shortcuts for the Documents tab — admins only. We only activate
  // when the focus isn't inside an input/textarea so we don't fight forms.
  useEffect(() => {
    if (!isAdmin) return;
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = (e.key || "").toLowerCase();
      if (k === "v" && clipboard) { e.preventDefault(); onPaste(); }
      // Copy/Cut keyboard shortcuts need a focused row — skip for v1 to avoid
      // accidental clipboard overrides while browsing.
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, clipboard, currentParentId]);

  const del = (d) => {
    const label = d.type === "folder" ? `folder "${d.name}" and everything inside it` : `"${d.name}"`;
    const kind = d.type === "folder" ? "folder" : "document";
    setConfirmState({
      title: `Move ${kind} to Recycle Bin?`,
      // Copy now reflects the new soft-delete flow: the item lands in
      // the Recycle Bin for 30 days and can be restored by any admin
      // during that window. Auto-purge only runs after retention lapses.
      message: `This will move ${label} to the Recycle Bin. It can be restored by an admin within 30 days from Room → Recycle bin. After 30 days it will be permanently deleted.`,
      confirmLabel: "Move to Recycle Bin",
      destructive: true,
      onConfirm: async () => {
        try {
          // Single API call — the backend cascades soft-delete to every
          // descendant automatically. No client-side BFS needed.
          await api.delete(`/rooms/${roomId}/documents/${d.id}`);
          await load();
          toast.success(
            d.type === "folder"
              ? "Folder moved to Recycle Bin"
              : "Document moved to Recycle Bin"
          );
        } catch (e) {
          toast.error(formatError(e));
          throw e;
        }
      },
    });
  };

  const download = async (d, fmt) => {
    // Per-file progress tracking — bar appears at the bottom of the
    // viewport, fills as bytes arrive (when Content-Length is known) or
    // shows an indeterminate stripe otherwise. Labelled with the document
    // name + format so a user can tell which click is in flight.
    const fmtLabel =
      fmt === "encrypted" ? "Encrypted PDF"
      : fmt === "pdf"     ? "Flat PDF"
      : "Original";
    const taskId = fileProgress.start({
      label: `Preparing ${fmtLabel} — ${d.name}`,
    });
    try {
      const res = await api.get(`/rooms/${roomId}/documents/${d.id}/download`, {
        params: { fmt },
        responseType: "blob",
        onDownloadProgress: (evt) => {
          fileProgress.update(taskId, {
            loaded: evt.loaded,
            total: evt.total || evt.event?.target?.getResponseHeader?.("content-length"),
          });
        },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      // `fmt=encrypted` is now a watermarked + locked PDF, not a `.enc`
      // package. `fmt=pdf` is the clean flat PDF.
      const base = d.name.replace(/\.[^.]+$/, "");
      a.download =
        fmt === "encrypted" ? `${base}.encrypted.pdf`
        : fmt === "pdf"     ? `${base}.pdf`
        : d.name;
      a.click(); URL.revokeObjectURL(url);
      fileProgress.setLabel(taskId, `Ready — ${a.download}`);
      fileProgress.complete(taskId);
    } catch (e) {
      fileProgress.error(taskId, formatError(e));
      toast.error(formatError(e));
    }
  };

  const fmtSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  // File-type icon mapping. Returns [IconComponent, colorClass].
  const fileIcon = (d) => {
    const name = (d.name || "").toLowerCase();
    const mime = (d.mime || "").toLowerCase();
    if (mime.includes("pdf") || name.endsWith(".pdf")) return [FileType, "text-rose-500"];
    if (mime.includes("word") || /\.docx?$/i.test(name)) return [FileText, "text-blue-500"];
    if (mime.includes("sheet") || /\.xlsx?$/i.test(name) || /\.csv$/i.test(name)) return [FileSpreadsheet, "text-emerald-500"];
    if (mime.includes("presentation") || /\.pptx?$/i.test(name)) return [Presentation, "text-orange-500"];
    if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(name)) return [ImageIcon, "text-purple-500"];
    if (mime.startsWith("text/") || /\.(txt|md|json|xml)$/i.test(name)) return [FileText, "text-slate-500 dark:text-slate-300"];
    return [FileText, "text-muted-foreground"];
  };

  const renderRow = (d) => {
    const [Icon, color] = d.type === "folder"
      ? [Folder, "text-primary"]
      : fileIcon(d);
    // Compute effective perms for BOTH files and folders so the viewer can
    // see (a) which folders they can enter at all and (b) the access-level
    // badge that summarises their rights.
    const p = permsFor(d);
    const canView = p?.view;
    // The "flat PDF" download is only meaningful for DOCX (converts) and
    // PDF (locks). For images, spreadsheets, etc. the backend would 415.
    const isConvertible = d.type === "file" && (
      /\.(docx?|pdf)$/i.test(d.name || "") ||
      /pdf|wordprocessingml|msword/i.test(d.mime || "")
    );
    const canPdf = p?.pdf && isConvertible;
    // "Encrypted PDF" is the watermarked + locked variant — same source
    // constraints (DOCX or PDF) since the backend can only stamp PDFs.
    const canEnc = p?.encrypted && isConvertible;
    const canOrig = p?.original;
    const isDropTarget = d.type === "folder" && dragOverId === d.id;
    return (
      <div
        key={d.id} data-testid={DOC.row}
        draggable={isAdmin && renameId !== d.id}
        onDragStart={(e) => onRowDragStart(e, d)}
        onDragOver={d.type === "folder" ? (e) => onFolderDragOver(e, d.id) : undefined}
        onDragLeave={d.type === "folder" ? onFolderDragLeave : undefined}
        onDrop={d.type === "folder" ? (e) => onFolderDrop(e, d.id) : undefined}
        onContextMenu={(e) => openCtxMenu(e, d)}
        className={`grid grid-cols-[1fr_120px_100px_240px] items-center border-b border-border last:border-b-0 transition ${
          isDropTarget ? "bg-primary/10 outline outline-2 outline-primary" : "hover:bg-secondary/50"
        }`}
      >
        <div className="px-4 py-3 flex items-center gap-3 min-w-0">
          {renameId === d.id ? (
            <div className="flex items-center gap-3 min-w-0 w-full">
              <Icon className={`w-4 h-4 shrink-0 ${color}`} />
              <input
                data-testid="doc-rename-input"
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); saveRename(d); }
                  if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                }}
                onBlur={() => saveRename(d)}
                className="flex-1 min-w-0 px-2 py-1 text-sm bg-background border border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          ) : d.type === "folder" ? (
            <button
              onClick={() => enter(d)}
              data-testid="folder-open-btn"
              className="flex items-center gap-3 min-w-0 text-left w-full"
            >
              <Icon className={`w-4 h-4 shrink-0 ${color}`} />
              <span className="truncate text-sm hover:underline">{d.name}</span>
              <AccessBadge perms={p} isAdmin={isAdmin} isFolder />
            </button>
          ) : (
            canView ? (
              <Link
                to={`/app/rooms/${roomId}/view/${d.id}`}
                data-testid="doc-open-link"
                className="flex items-center gap-3 min-w-0 text-left w-full hover:underline"
                title="Open document (fence view)"
              >
                <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                <span className="truncate text-sm">{d.name}</span>
                <AccessBadge perms={p} isAdmin={isAdmin} />
              </Link>
            ) : (
              <div
                className="flex items-center gap-3 min-w-0 text-left w-full opacity-70 cursor-not-allowed"
                title="You do not have view permission for this document"
              >
                <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                <span className="truncate text-sm">{d.name}</span>
                <AccessBadge perms={p} isAdmin={isAdmin} />
              </div>
            )
          )}
        </div>
        <div className="px-4 py-3 mono text-xs text-muted-foreground">
          {d.type === "folder"
            ? `${docs.filter((x) => x.parent_id === d.id).length} items`
            : fmtSize(d.size)}
        </div>
        <div className="px-4 py-3 mono text-xs text-muted-foreground">{d.type === "file" ? d.pages : "—"}</div>
        <div className="px-4 py-3 flex justify-end gap-1">
          {d.type === "file" && (
            <>
              {canView && (
                <Link
                  to={`/app/rooms/${roomId}/view/${d.id}`}
                  data-testid={DOC.viewBtn}
                  className="p-2 rounded-md text-emerald-600 hover:bg-emerald-500/10 transition" title="Fence view"
                >
                  <Eye className="w-3.5 h-3.5" />
                </Link>
              )}
              {canPdf && (
                <button
                  data-testid="doc-download-pdf-btn"
                  onClick={() => download(d, "pdf")}
                  className="p-2 rounded-md text-rose-500 hover:bg-rose-500/10 transition"
                  title="Download as flat (non-editable) PDF"
                >
                  <PdfDownloadIcon />
                </button>
              )}
              {canEnc && isConvertible && (
                <button
                  data-testid={DOC.downloadEncBtn}
                  onClick={() => download(d, "encrypted")}
                  className="p-2 rounded-md text-amber-600 hover:bg-amber-500/10 transition"
                  title="Download Encrypted PDF (watermarked · view-only · no edit)"
                >
                  <Lock className="w-3.5 h-3.5" />
                </button>
              )}
              {canOrig && (
                <button data-testid={DOC.downloadOrigBtn} onClick={() => download(d, "original")} className="p-2 rounded-md text-sky-600 hover:bg-sky-500/10 transition" title="Download Original">
                  <FileDown className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <button
                data-testid="doc-rename-btn"
                onClick={() => startRename(d)}
                className="p-2 rounded-md text-indigo-500 hover:bg-indigo-500/10 transition"
                title="Rename"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="doc-manage-access-btn"
                onClick={() => setAccessDoc(d)}
                className={`p-2 rounded-md transition ${d.restricted ? "text-purple-600 hover:bg-purple-500/10" : "text-muted-foreground hover:bg-secondary"}`}
                title={d.restricted ? "Restricted access — click to manage" : "Manage access"}
              >
                <Link2 className="w-3.5 h-3.5" />
              </button>
              <button data-testid={DOC.deleteBtn} onClick={() => del(d)} className="p-2 rounded-md text-rose-600 hover:bg-destructive hover:text-destructive-foreground transition" title="Move to Recycle Bin">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Search bar */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="doc-search-input"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search by file name or text content within PDFs…"
          className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
        {searchQ && (
          <button
            onClick={() => setSearchQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* SEARCH RESULTS view (overrides the folder browser) */}
      {searchHits !== null ? (
        (() => {
          const visibleHits = searchHits.filter((h) => {
            if (!myPerms) return true; // hide nothing until perms resolve
            if (myPerms.is_admin) return true;
            const base = myPerms.overrides?.[h.id] || myPerms.room_level;
            return !!base?.view;
          });
          return (
        <div>
          <div className="label-mono text-muted-foreground mb-3">
            {searching ? "Searching…" : `${visibleHits.length} result${visibleHits.length !== 1 ? "s" : ""} for "${searchQ}"`}
          </div>
          {visibleHits.length === 0 && !searching ? (
            <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              No matches you have permission to view.
            </div>
          ) : (
            <div className="border border-border">
              {visibleHits.map((h) => {
                const [Icon, color] = fileIcon(h);
                return (
                  <Link
                    key={h.id}
                    to={`/app/rooms/${roomId}/view/${h.id}`}
                    className="block px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate font-medium">{h.name}</div>
                        <div className="mono text-[10px] text-muted-foreground">
                          {h.path || "(root)"} · {fmtSize(h.size)} · {h.pages} page{h.pages !== 1 ? "s" : ""}
                          {!h.match_name && h.snippet && " · text match"}
                        </div>
                        {h.snippet && !h.match_name && (
                          <div className="text-xs text-muted-foreground mt-1 italic truncate max-w-3xl">
                            “{h.snippet}”
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
          );
        })()
      ) : (
        <>
          {/* Breadcrumbs — also act as drop-zones for moving items up the tree */}
          <nav className="flex items-center gap-1.5 text-sm mb-4 flex-wrap">
            <button
              onClick={goRoot}
              onDragOver={isAdmin ? (e) => onFolderDragOver(e, null) : undefined}
              onDragLeave={isAdmin ? onFolderDragLeave : undefined}
              onDrop={isAdmin ? (e) => onFolderDrop(e, null) : undefined}
              className={`px-2 py-1 rounded-md hover:bg-secondary transition ${
                path.length === 0 ? "text-foreground font-medium" : "text-muted-foreground"
              } ${dragOverId === null && dragOverId !== undefined ? "" : ""} ${
                dragOverId === "__ROOT__" ? "bg-primary/10 outline outline-2 outline-primary" : ""
              }`}
            >
              {room?.name || "Data Room"}
            </button>
            {path.map((p, i) => (
              <React.Fragment key={p.id}>
                <span className="text-muted-foreground">/</span>
                <button
                  onClick={() => goTo(i)}
                  onDragOver={isAdmin ? (e) => onFolderDragOver(e, p.id) : undefined}
                  onDragLeave={isAdmin ? onFolderDragLeave : undefined}
                  onDrop={isAdmin ? (e) => onFolderDrop(e, p.id) : undefined}
                  className={`px-2 py-1 rounded-md hover:bg-secondary transition truncate max-w-[240px] ${
                    i === path.length - 1 ? "text-foreground font-medium" : "text-muted-foreground"
                  } ${dragOverId === p.id ? "bg-primary/10 outline outline-2 outline-primary" : ""}`}
                >
                  {p.name}
                </button>
              </React.Fragment>
            ))}
            {isAdmin && (
              <span className="ml-3 label-mono text-muted-foreground hidden md:inline">
                · Drag to move · Right-click for copy/cut/paste
              </span>
            )}
            {!isAdmin && path.length > 0 && (
              <button
                type="button"
                onClick={() => setRequestScope({
                  docId: path[path.length - 1].id,
                  name: path[path.length - 1].name,
                  isFolder: true,
                })}
                data-testid="request-access-folder"
                title="Ask an App Owner for scoped access to this folder"
                className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-500 hover:bg-emerald-500/10 mono text-[10px] uppercase tracking-wider transition"
              >
                <KeyRound className="w-3 h-3" /> Request access here
              </button>
            )}
          </nav>
          {clipboard && isAdmin && (
            <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary border border-border text-xs">
              <span className={`mono uppercase tracking-wider ${clipboard.mode === "copy" ? "text-sky-500" : "text-amber-500"}`}>
                {clipboard.mode === "copy" ? "Copy" : "Cut"}
              </span>
              <span className="truncate max-w-[260px]">{clipboard.doc.name}</span>
              <button
                onClick={onPaste}
                className="ml-2 px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition"
              >Paste here</button>
              <button
                onClick={() => setClipboard(null)}
                className="px-2 py-1 rounded hover:bg-secondary-foreground/10 transition"
              >Clear</button>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="label-mono text-muted-foreground">
              — {visible.length} item{visible.length !== 1 && "s"}{path.length > 0 ? ` in this folder` : ""}
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <button
                  data-testid={DOC.folderBtn}
                  onClick={() => setShowFolder(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
                >
                  <FolderPlus className="w-4 h-4" /> New folder
                </button>
              )}
              {isAdmin && (
                <button
                  data-testid="doc-convert-pdf-btn"
                  onClick={() => setShowConvert(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
                  title="Batch convert Word documents to flat (non-editable) PDFs"
                >
                  <FileType2 className="w-4 h-4 text-blue-500" /> Word → PDF
                </button>
              )}
              {isAdmin && <WarmCacheButton roomId={roomId} />}
              {/* Visible to anyone — backend filters by per-user perms */}
              {(myPerms?.is_admin || (myPerms?.allow_downloads !== false &&
                (myPerms?.room_level?.pdf || myPerms?.room_level?.encrypted || myPerms?.room_level?.original))) && (
                <button
                  data-testid="doc-download-zip-btn"
                  onClick={() => setShowZip(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
                  title="Download every file you have access to as a ZIP"
                >
                  <Archive className="w-4 h-4 text-emerald-500" /> Download all
                </button>
              )}
              {(myPerms?.is_admin || myPerms?.room_level?.upload) && (
                <button
                  data-testid={DOC.uploadBtn}
                  onClick={() => setShowBulk((v) => !v)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
                >
                  <Upload className="w-4 h-4" /> {showBulk ? "Hide uploader" : "Upload here"}
                </button>
              )}
            </div>
          </div>
          {err && <div className="text-sm text-destructive mb-3">{err}</div>}

          {showBulk && (myPerms?.is_admin || myPerms?.room_level?.upload) && (
            <div className="mb-4">
              <BulkUpload roomId={roomId} parentId={currentParentId} onDone={load} />
            </div>
          )}

          <div className="border border-border">
            <div className="grid grid-cols-[1fr_120px_100px_240px] label-mono text-muted-foreground border-b border-border bg-secondary">
              <SortHeader
                label="Name"
                active={sortKey === "name"}
                dir={sortDir}
                onClick={() => toggleSort("name")}
                testId="doc-sort-name"
              />
              <SortHeader
                label="Size"
                active={sortKey === "size"}
                dir={sortDir}
                onClick={() => toggleSort("size")}
                testId="doc-sort-size"
              />
              <SortHeader
                label="Pages"
                active={sortKey === "pages"}
                dir={sortDir}
                onClick={() => toggleSort("pages")}
                testId="doc-sort-pages"
              />
              <div className="px-4 py-3 text-right">Actions</div>
            </div>
            {visible.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                {path.length === 0
                  ? "No documents yet. Create a folder or upload your first file."
                  : "This folder is empty. Upload files or create a sub-folder."}
              </div>
            ) : visible.map(renderRow)}
          </div>
        </>
      )}

      {showFolder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowFolder(false)}>
          <form className="bg-card border border-border rounded-md w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={createFolder}>
            <h3 className="font-display text-xl tracking-tight">New folder</h3>
            <div className="text-xs text-muted-foreground">
              Creating in: <span className="font-mono">{path.length ? path.map((p) => p.name).join(" / ") : "root"}</span>
            </div>
            <input
              data-testid={DOC.folderName}
              required value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button data-testid={DOC.folderSubmit} type="submit" className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90">Create</button>
          </form>
        </div>
      )}

      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
      {showConvert && isAdmin && (
        <BatchConvertDialog
          roomId={roomId}
          docs={docs}
          currentParentId={currentParentId}
          currentPath={path}
          onClose={() => setShowConvert(false)}
          onDone={async () => { await load(); }}
        />
      )}
      {showZip && (
        <BatchDownloadZipDialog
          roomId={roomId}
          myPerms={myPerms}
          onClose={() => setShowZip(false)}
        />
      )}
      {accessDoc && (
        <ManageAccessDialog
          roomId={roomId}
          doc={accessDoc}
          onClose={() => setAccessDoc(null)}
          onSaved={() => load()}
        />
      )}
      {requestScope && (
        <RequestAccessModal
          roomId={roomId}
          docId={requestScope.docId}
          docName={requestScope.name}
          isFolder={requestScope.isFolder}
          onClose={() => setRequestScope(null)}
          onDone={() => setRequestScope(null)}
        />
      )}

      {/* Right-click context menu.
          Files: visible to every user; shows only the download formats
          they're allowed to fetch (per permsFor(d)) plus admin-only
          management actions below the divider.
          Folders: openCtxMenu blocks non-admins upstream, so this
          branch only ever runs with an admin viewer. */}
      {ctxMenu && (() => {
        const doc = ctxMenu.doc;
        const p = doc.type === "folder" ? null : permsFor(doc);
        const canDownloadOrig = !!(p && p.original);
        const canDownloadPdf  = !!(p && p.pdf);
        const canDownloadEnc  = !!(p && p.encrypted);
        const anyDownload = canDownloadOrig || canDownloadPdf || canDownloadEnc;
        // Non-admin viewing a file with no download permission → still
        // open the menu but tell them clearly why nothing is actionable,
        // so they don't think the app is broken.
        const showEmptyDownloadHint = !isAdmin && doc.type === "file" && !anyDownload;
        return (
        <ContextMenuContainer
          anchorX={ctxMenu.x}
          anchorY={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        >
          <div className="px-3 py-1 label-mono text-muted-foreground truncate max-w-[220px]">{doc.name}</div>

          {/* Download section — the ONE section every viewer with the
              right permission can use. Order matches the row icons:
              Original > Encrypted PDF > Flat PDF. */}
          {doc.type === "file" && (
            <>
              <div className="border-t border-border my-1" />
              {canDownloadOrig && (
                <CtxItem
                  icon={FileDown}
                  label="Download original"
                  onClick={() => { download(doc, "original"); setCtxMenu(null); }}
                  testid="ctx-download-original"
                />
              )}
              {canDownloadEnc && (
                <CtxItem
                  icon={Lock}
                  label="Download encrypted PDF"
                  onClick={() => { download(doc, "encrypted"); setCtxMenu(null); }}
                  testid="ctx-download-encrypted"
                />
              )}
              {canDownloadPdf && (
                <CtxItem
                  icon={FileType2}
                  label="Download PDF"
                  onClick={() => { download(doc, "pdf"); setCtxMenu(null); }}
                  testid="ctx-download-pdf"
                />
              )}
              {showEmptyDownloadHint && (
                <div className="px-3 py-1.5 text-xs text-muted-foreground italic" data-testid="ctx-no-download">
                  No download permission for this file
                </div>
              )}
            </>
          )}

          {/* Admin-only management actions — non-admins never see these. */}
          {isAdmin && (
            <>
              <div className="border-t border-border my-1" />
              <CtxItem icon={FileText} label="Copy" hint="⌘C" onClick={() => onCopy(doc)} testid="ctx-copy" />
              <CtxItem icon={ArrowLeft} label="Cut" hint="⌘X" onClick={() => onCut(doc)} testid="ctx-cut" />
              <CtxItem
                icon={FolderPlus} label={clipboard ? `Paste ${clipboard.mode === "copy" ? "copy" : "move"} here` : "Paste"}
                hint="⌘V" onClick={onPaste} disabled={!clipboard} testid="ctx-paste"
              />
              <div className="border-t border-border my-1" />
              <CtxItem icon={Pencil} label="Rename" onClick={() => { startRename(doc); setCtxMenu(null); }} testid="ctx-rename" />
              <CtxItem icon={Link2} label="Manage access" onClick={() => { setAccessDoc(doc); setCtxMenu(null); }} testid="ctx-access" />
              <div className="border-t border-border my-1" />
              <CtxItem icon={Trash2} label="Move to Recycle Bin" destructive onClick={() => { del(doc); setCtxMenu(null); }} testid="ctx-delete" />
            </>
          )}
        </ContextMenuContainer>
        );
      })()}
    </div>
  );
}

function CtxItem({ icon: Icon, label, hint, onClick, disabled, destructive, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between px-3 py-1.5 transition ${
        destructive ? "text-rose-500 hover:bg-rose-500/10" : "hover:bg-secondary"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <span className="inline-flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      {hint && <span className="mono text-[10px] text-muted-foreground">{hint}</span>}
    </button>
  );
}

/**
 * ContextMenuContainer — positions a right-click menu so it never
 * clips outside the viewport, regardless of how many items it holds.
 *
 * The old implementation clamped the anchor with a HARDCODED assumed
 * menu height (320px). That worked for the average admin menu but
 * clipped when the doc had many download options + admin controls
 * (~360px+) or when the click happened near the bottom of the page.
 *
 * How it works:
 *   1. First paint: render invisibly at the anchor so we can measure
 *      the actual bounding rect once the DOM lays out.
 *   2. useLayoutEffect: read the real width/height, flip the menu
 *      up/left if the natural position would spill past the viewport,
 *      then unhide with the corrected top/left. `useLayoutEffect`
 *      (not `useEffect`) runs BEFORE paint, so there's no flash of
 *      mis-positioned menu.
 *   3. On subsequent window resize / scroll we recompute — cheap and
 *      handles the rare case where the viewport shrinks while the
 *      menu is open.
 *
 * Callers pass the raw click coordinates via ``anchorX / anchorY``
 * (from the MouseEvent) and the children they want inside.
 */
function ContextMenuContainer({ anchorX, anchorY, children, testid = "doc-context-menu", onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: anchorY, left: anchorX, visible: false });

  useLayoutEffect(() => {
    // 12 px breathing room so the menu doesn't kiss the viewport edge.
    const PADDING = 12;
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = anchorX;
      let top = anchorY;
      // Horizontal: flip left if right edge would overflow.
      if (left + rect.width + PADDING > vw) {
        left = Math.max(PADDING, vw - rect.width - PADDING);
      }
      // Vertical: flip up if bottom edge would overflow. If the menu
      // is taller than the viewport (rare), pin to the top with a
      // small padding — the browser will show a scrollbar on the
      // menu's own overflow rules if needed.
      if (top + rect.height + PADDING > vh) {
        top = Math.max(PADDING, vh - rect.height - PADDING);
      }
      setPos({ top, left, visible: true });
    };
    compute();
    // Handle viewport changes while the menu is open.
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [anchorX, anchorY]);

  // Close on Escape — muscle memory for every context menu since 1995.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed z-50 min-w-[220px] max-h-[85vh] overflow-y-auto bg-popover border border-border rounded-md shadow-xl py-1.5 text-sm"
      style={{
        top: pos.top,
        left: pos.left,
        // Hide until layout has measured + clamped, otherwise there
        // would be a single-frame flash at the raw anchor point.
        visibility: pos.visible ? "visible" : "hidden",
      }}
    >
      {children}
    </div>
  );
}

/* ----------------- PDF download icon (custom inline SVG) ----------------- */
/* Lucide doesn't ship a recognisable "PDF" mark, so we draw a doc outline
   with a red "PDF" label inside — instantly readable to any user. */
function PdfDownloadIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <polyline
        points="14 2 14 8 20 8"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <text
        x="12" y="18" textAnchor="middle"
        fontSize="6.5" fontWeight="700" letterSpacing="0.4"
        fill="currentColor"
      >PDF</text>
    </svg>
  );
}

/* --------------- Per-row effective-access badge (viewer side) ----------- */
/* Summarises what THE CURRENT USER can do with a given row at a glance.
   Hidden for admins (they always have full rights) and for the "Full" case
   to keep the table clean — restricted access is what the viewer cares
   about, not the absence of restrictions. */
function AccessBadge({ perms, isAdmin, isFolder }) {
  if (isAdmin || !perms) return null;
  const can = (k) => !!perms[k];
  // Determine the strongest available action.
  let label, tone, hint;
  if (!can("view")) {
    label = "No access";
    tone = "bg-rose-500/15 text-rose-400 border-rose-500/30";
    hint = "You cannot open this item";
  } else if (can("original")) {
    label = "Full";
    tone = "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    hint = "View · Download original · Download PDF/Encrypted";
  } else if (can("pdf") || can("encrypted")) {
    // "Encrypted PDF" is the stronger forensic variant — surface it
    // explicitly when it's the strongest non-original right the viewer has.
    label = can("encrypted") ? "View + Encrypted PDF" : "View + PDF";
    tone = "bg-sky-500/15 text-sky-400 border-sky-500/30";
    hint = can("encrypted")
      ? "You can view and download a watermarked, view-only PDF (no edit / no copy)"
      : "You can view and download a flat (non-editable) PDF";
  } else {
    label = "View only";
    tone = "bg-amber-500/15 text-amber-400 border-amber-500/30";
    hint = isFolder
      ? "You can open folders but cannot download contents"
      : "You can open the file in fence view — no downloads";
  }
  return (
    <span
      data-testid="row-access-badge"
      title={hint}
      className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap ${tone}`}
    >
      {label}
    </span>
  );
}

/* --------------------- Batch Convert Word → PDF dialog -------------------- */

function isWordDoc(d) {
  const mime = (d.mime || "").toLowerCase();
  const name = (d.name || "").toLowerCase();
  return mime.includes("wordprocessingml") || mime.includes("msword") || name.endsWith(".docx");
}

/** Compute every descendant folder id beneath `rootId`, inclusive of rootId.
 *  Used by the "current folder + subfolders" scope so we can match by
 *  parent_id without recursing on the server. */
function descendantFolderIds(allDocs, rootId) {
  const ids = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const d of allDocs) {
      if (d.type === "folder" && !ids.has(d.id) && ids.has(d.parent_id)) {
        ids.add(d.id);
        changed = true;
      }
    }
  }
  return ids;
}

function BatchConvertDialog({ roomId, docs, currentParentId, currentPath, onClose, onDone }) {
  // Default scope: if you're inside a folder, scope to that folder; at the
  // room root we default to the whole room (the most natural intent).
  const initialScope = currentParentId ? "current" : "all";
  const [scope, setScope] = useState(initialScope);
  const [mode, setMode] = useState("alongside");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Build the candidate list based on scope. The dialog is re-derived
  // whenever scope changes so the count + selection stay in sync.
  const candidates = useMemo(() => {
    const files = (docs || []).filter((d) => d.type === "file" && isWordDoc(d));
    if (scope === "all") return files;
    if (scope === "current") {
      return files.filter((d) => d.parent_id === currentParentId);
    }
    // scope === "subtree"  — current folder + all descendants
    const folderIds = descendantFolderIds(docs || [], currentParentId);
    return files.filter((d) => folderIds.has(d.parent_id));
  }, [docs, scope, currentParentId]);

  const [selected, setSelected] = useState(() => new Set());
  // Reset the selection when the candidate list (i.e. scope) changes so we
  // never silently include files that are no longer visible in the list.
  React.useEffect(() => {
    setSelected(new Set(candidates.map((d) => d.id)));
  }, [candidates]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((d) => d.id)));
  };

  const folderLabel = currentPath?.length
    ? currentPath.map((p) => p.name).join(" / ")
    : "Room root";

  const scopeOptions = [
    {
      v: "current",
      label: "This folder only",
      hint: `Only Word files directly in "${folderLabel}"`,
      disabled: !currentParentId, // at the root, "current" === "all" so skip
    },
    {
      v: "subtree",
      label: "This folder + subfolders",
      hint: `Everything under "${folderLabel}"`,
      disabled: !currentParentId,
    },
    {
      v: "all",
      label: "Entire data room",
      hint: "Every Word document in every folder",
      disabled: false,
    },
  ];

  const run = async () => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/rooms/${roomId}/documents/batch-convert-to-pdf`, {
        doc_ids: [...selected],
        mode,
      });
      setResult(data);
      toast.success(
        `Converted ${data.converted.length} Word doc${data.converted.length === 1 ? "" : "s"} to PDF` +
          (data.failed.length ? ` · ${data.failed.length} failed` : "") +
          (data.skipped.length ? ` · ${data.skipped.length} skipped` : "")
      );
      await onDone?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog" aria-modal="true" aria-label="Batch convert Word documents to PDF"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      data-testid="doc-convert-dialog"
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display tracking-tight text-lg inline-flex items-center gap-2">
              <FileType2 className="w-5 h-5 text-blue-500" />
              Convert Word documents to PDF
            </h3>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Each converted file is a flat, non-editable PDF (modify, copy &amp; high-res print disabled).
              Use this to lock documents before sharing.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-md hover:bg-secondary transition shrink-0 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scope selector — defaults to the folder the admin is in. */}
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono text-muted-foreground">Scope</div>
            <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground truncate max-w-[60%]">
              <Folder className="w-3 h-3 inline mr-1 mb-0.5" />
              {folderLabel}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {scopeOptions.map(({ v, label, hint, disabled }) => (
              <button
                key={v} type="button"
                data-testid={`doc-convert-scope-${v}`}
                onClick={() => !busy && !disabled && setScope(v)}
                aria-pressed={scope === v}
                disabled={busy || disabled}
                className={`text-left p-2.5 rounded-md border transition ${
                  scope === v
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-secondary/40"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  {scope === v
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                    : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
                  {label}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 leading-snug truncate">{hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Mode selector */}
        <div className="px-5 py-4 border-b border-border">
          <div className="label-mono text-muted-foreground mb-2">Output mode</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { v: "alongside", label: "Keep originals", hint: "Add new .pdf files in the same folder" },
              { v: "replace",   label: "Replace originals", hint: "Overwrite the .docx with the new PDF (permanent)" },
            ].map(({ v, label, hint }) => (
              <button
                key={v} type="button"
                data-testid={`doc-convert-mode-${v}`}
                onClick={() => !busy && setMode(v)}
                aria-pressed={mode === v}
                disabled={busy}
                className={`text-left p-3 rounded-md border transition ${
                  mode === v
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-secondary/40"
                } disabled:opacity-60`}
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  {mode === v
                    ? <CheckCircle2 className="w-4 h-4 text-primary" />
                    : <Circle className="w-4 h-4 text-muted-foreground" />}
                  {label}
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{hint}</div>
              </button>
            ))}
          </div>
          {mode === "replace" && (
            <div className="mt-3 text-xs text-amber-500 inline-flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" />
              Originals are permanently removed once the new PDF is saved.
            </div>
          )}
        </div>

        {/* File list */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="label-mono text-muted-foreground">
            {candidates.length === 0
              ? "No Word documents in this scope"
              : `${selected.size} of ${candidates.length} selected`}
          </div>
          {candidates.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              disabled={busy}
              className="text-xs text-muted-foreground hover:text-foreground transition disabled:opacity-40"
            >
              {selected.size === candidates.length ? "Deselect all" : "Select all"}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {candidates.length === 0 ? (
            <div className="px-5 py-8 text-sm text-muted-foreground text-center">
              No .docx files in this scope. Try a wider scope, or upload Word documents first.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {candidates.map((d) => {
                const on = selected.has(d.id);
                return (
                  <li
                    key={d.id}
                    data-testid={`doc-convert-row-${d.id}`}
                    onClick={() => !busy && toggle(d.id)}
                    className={`px-5 py-2.5 flex items-center gap-3 cursor-pointer transition ${
                      on ? "bg-primary/[0.04]" : "hover:bg-secondary/40"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (!busy) toggle(d.id); }}
                      disabled={busy}
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        on ? "bg-primary border-primary text-primary-foreground" : "border-border"
                      }`}
                      aria-pressed={on}
                    >
                      {on && <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{d.name}</div>
                      <div className="mono text-[10px] text-muted-foreground">
                        {((d.size || 0) / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Result summary */}
        {result && (
          <div className="px-5 py-3 border-t border-border text-xs space-y-1 bg-secondary/30">
            {result.converted.length > 0 && (
              <div className="text-primary inline-flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {result.converted.length} converted
                {result.mode === "alongside" ? " (saved alongside originals)" : " (originals replaced)"}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="text-amber-500">— {result.skipped.length} skipped (not Word documents)</div>
            )}
            {result.failed.length > 0 && (
              <div className="text-destructive">— {result.failed.length} failed</div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition disabled:opacity-60"
          >
            {result ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            data-testid="doc-convert-submit"
            onClick={run}
            disabled={busy || selected.size === 0 || result?.converted.length > 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
          >
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Converting…</>) : (
              <><FileType2 className="w-4 h-4" /> Convert {selected.size} file{selected.size === 1 ? "" : "s"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Batch ZIP download dialog ----------------------- */

function BatchDownloadZipDialog({ roomId, myPerms, onClose }) {
  // Format options are gated by what the viewer can actually download.
  // Admins always see all three; everyone else only the ones that match
  // at least one permission row in the room.
  const room = myPerms?.room_level || {};
  const overrides = Object.values(myPerms?.overrides || {});
  const has = (k) => myPerms?.is_admin || room[k] || overrides.some((o) => o?.[k]);
  const availableFmts = ["pdf", "encrypted", "original"].filter(has);
  const [fmt, setFmt] = useState(availableFmts[0] || "pdf");
  const [busy, setBusy] = useState(false);

  const labels = {
    pdf: {
      label: "Flat PDFs",
      hint: "Locked, non-editable PDFs (Word docs converted on-the-fly). No watermark.",
    },
    encrypted: {
      label: "Encrypted PDFs",
      hint: "Watermarked, view-only, no edit / no print / no copy. Each page carries your identity.",
    },
    original: {
      label: "Originals",
      hint: "Raw source files in their original format (.docx, .pdf, .xlsx, etc.).",
    },
  };

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.get(`/rooms/${roomId}/download-archive`, {
        params: { fmt }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(cd);
      a.href = url;
      a.download = m ? decodeURIComponent(m[1]) : `data-room-${fmt}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archive downloaded.");
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog" aria-modal="true" aria-label="Download data room as ZIP"
      data-testid="doc-zip-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display tracking-tight text-lg inline-flex items-center gap-2">
              <Archive className="w-5 h-5 text-emerald-500" />
              Download data room as ZIP
            </h3>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              The archive will only include files you have permission to download.
              Folder structure is preserved.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-md hover:bg-secondary transition shrink-0 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-2">
          {availableFmts.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              You don&apos;t have download permission on this room.
            </div>
          ) : (
            availableFmts.map((v) => (
              <button
                key={v} type="button"
                data-testid={`doc-zip-fmt-${v}`}
                onClick={() => !busy && setFmt(v)}
                aria-pressed={fmt === v}
                disabled={busy}
                className={`w-full text-left p-3 rounded-md border transition ${
                  fmt === v
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-secondary/40"
                } disabled:opacity-60`}
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  {fmt === v
                    ? <CheckCircle2 className="w-4 h-4 text-primary" />
                    : <Circle className="w-4 h-4 text-muted-foreground" />}
                  {labels[v].label}
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{labels[v].hint}</div>
              </button>
            ))
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="doc-zip-submit"
            onClick={run}
            disabled={busy || availableFmts.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
          >
            {busy
              ? (<><Loader2 className="w-4 h-4 animate-spin" /> Preparing archive…</>)
              : (<><Archive className="w-4 h-4" /> Download ZIP</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

